import React, { useEffect, useState } from 'react';
import { Button, Spinner } from '../ui';
import { feedbackApi } from '../../services/feedbackApi';

const TYPES = [
  { value: 'bug', label: 'Bug' },
  { value: 'feature', label: 'Feature request' },
];

export function FeedbackDrawer({ open, onClose }) {
  const [type, setType] = useState('bug');
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape' && !isSubmitting) handleClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isSubmitting]);

  const resetForm = () => {
    setType('bug');
    setDescription('');
    setError(null);
    setSuccess(false);
  };

  const handleClose = () => {
    if (isSubmitting) return;
    resetForm();
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!description.trim()) {
      setError('Please describe what you saw.');
      return;
    }
    setIsSubmitting(true);
    setError(null);
    try {
      await feedbackApi.submitFeedback({
        type,
        description: description.trim(),
      });
      setSuccess(true);
      setTimeout(() => {
        setIsSubmitting(false);
        resetForm();
        onClose();
      }, 1500);
    } catch (err) {
      console.error('Failed to submit feedback:', err);
      setError(err?.message || 'Failed to submit feedback. Please try again.');
      setIsSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black bg-opacity-40 transition-opacity"
        onClick={handleClose}
      />

      {/* Drawer */}
      <div
        role="dialog"
        aria-label="Send feedback"
        className="absolute inset-y-0 right-0 w-full max-w-md bg-white dark:bg-gray-900 shadow-xl flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-800">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Send feedback</h3>
          <button
            type="button"
            onClick={handleClose}
            disabled={isSubmitting}
            className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-200 disabled:opacity-50"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="flex flex-col flex-1">
          <div className="px-6 py-4 space-y-4 flex-1 overflow-y-auto">
            {error && (
              <div className="p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300 text-sm">
                {error}
              </div>
            )}
            {success && (
              <div className="p-3 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg text-green-700 dark:text-green-300 text-sm">
                Thanks — reported!
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Type</label>
              <div className="inline-flex p-1 bg-gray-100 dark:bg-gray-800 rounded-lg">
                {TYPES.map((t) => {
                  const active = type === t.value;
                  return (
                    <button
                      key={t.value}
                      type="button"
                      onClick={() => setType(t.value)}
                      disabled={isSubmitting}
                      className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
                        active
                          ? 'bg-blue-600 text-white shadow'
                          : 'text-gray-700 dark:text-gray-200 hover:text-gray-900 dark:hover:text-white'
                      }`}
                    >
                      {t.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <label
                htmlFor="feedback-description"
                className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1"
              >
                Describe what you see <span className="text-red-500">*</span>
              </label>
              <textarea
                id="feedback-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe what you see…"
                rows={8}
                disabled={isSubmitting}
                className="w-full border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                autoFocus
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Your email and the current page URL are included automatically.
              </p>
            </div>
          </div>

          <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting || !description.trim()}>
              {isSubmitting ? (
                <>
                  <Spinner size="sm" className="mr-2" />
                  Sending…
                </>
              ) : (
                'Send feedback'
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default FeedbackDrawer;
